import InvoiceCheckbox from "./invoice/InvoiceCheckbox";
import InvoiceDataDisplay from "./invoice/InvoiceDataDisplay";
import InvoiceDataForm from "./invoice/InvoiceDataForm";
import React, { useMemo } from "react";
import {
    Alert,
    Box,
    Button,
    Typography
    } from "@mui/material";
import { ApplicationWithId } from "../../interfaces/IApplications";
import { AppUser } from "../../interfaces/IUsers";
import { Cancel, Edit, Save } from "@mui/icons-material";
import { useApplicationInvoice } from "../../hooks/useApplicationInvoice";

interface ApplicationInvoiceSectionProps {
  application: ApplicationWithId;
  user: AppUser | null;
  onUpdate: (applicationId: string, data: Partial<ApplicationWithId>) => Promise<{ success: boolean; error?: string }>;
}

/**
 * Komponent formularza faktury dla zgłoszenia
 * 
 * Zasady:
 * - Tryb edycji/readonly
 * - Walidacja przy zapisie
 * - Domyślnie wypełnia danymi z konta użytkownika
 * - Pozwala na zmianę danych specyficznych dla zgłoszenia
 */
export default function ApplicationInvoiceSection({
  application,
  user,
  onUpdate,
}: ApplicationInvoiceSectionProps) {
  const {
    formData,
    formErrors,
    isEditing,
    isSaving,
    handleChange,
    handleEdit,
    handleCancel,
    handleSave,
  } = useApplicationInvoice(application, user, onUpdate);

  // Sprawdź czy użytkownik ma uzupełnione dane faktury w profilu
  const hasCompleteInvoiceDataInProfile = useMemo(() => {
    if (!user || !user.isInvoiceNeeded || !user.invoiceData) {
      return false;
    }

    const requiredFields = [
      "name",
      "taxId",
      "street",
      "houseNumber",
      "postalCode",
      "city",
    ];

    return requiredFields.every((field) => {
      const value = user.invoiceData?.[field as keyof typeof user.invoiceData];
      return value && String(value).trim().length > 0;
    });
  }, [user]);

  return (
    <Box sx={{ mt: 2 }}>
      <InvoiceCheckbox
        checked={formData.isInvoiceNeeded || false}
        onChange={handleChange}
        disabled={isSaving}
        label="Chcę otrzymać fakturę za to zgłoszenie"
      />

      {formData.isInvoiceNeeded && (
        <Box sx={{ mt: 2 }}>
          {formErrors.save && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formErrors.save}
            </Alert>
          )}

          {!hasCompleteInvoiceDataInProfile && (
            <Alert 
              severity="info" 
              sx={{ 
                mb: 2, 
                border: 1, 
                borderColor: "info",
              }}
            >
              Uzupełnij dane do faktury w profilu użytkownika. Ułatwi to późniejsze wprowadzanie danych do faktur na innych zgłoszeniach.
            </Alert>
          )}

          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Dane do faktury
          </Typography>

          {!isEditing ? (
            <>
              <InvoiceDataDisplay formData={formData} />
              <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Edit fontSize="small" />}
                  onClick={handleEdit}
                  disabled={isSaving}
                  sx={{
                    py: 1,
                    px: 1.5,
                    fontSize: "0.75rem",
                    minWidth: 120,
                    borderWidth: 2,
                    fontWeight: 600,
                    color: "#6366f1",
                    borderColor: "#6366f1",
                    "&:hover": { backgroundColor: "#f1e7de", borderColor: "#6366f1" },
                  }}
                >
                  Edytuj
                </Button>
              </Box>
            </>
          ) : (
            <>
              <InvoiceDataForm
                formData={formData}
                formErrors={formErrors}
                isSaving={isSaving}
                onChange={handleChange}
              />
              <Box sx={{ display: "flex", gap: 2, mt: 3, justifyContent: "flex-end" }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Cancel fontSize="small" />}
                  onClick={handleCancel}
                  disabled={isSaving}
                  sx={{
                    py: 1,
                    px: 1.5,
                    fontSize: "0.75rem",
                    minWidth: 120,
                    borderWidth: 2,
                    fontWeight: 600,
                    color: "#6366f1",
                    borderColor: "#6366f1",
                    "&:hover": { backgroundColor: "#f1e7de", borderColor: "#6366f1" },
                  }}
                >
                  Anuluj
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<Save fontSize="small" />}
                  onClick={handleSave}
                  disabled={isSaving}
                  sx={{
                    py: 1,
                    px: 1.5,
                    fontSize: "0.75rem",
                    minWidth: 120,
                    fontWeight: 600,
                    backgroundColor: "#6366f1",
                    "&:hover": { backgroundColor: "#4f46e5" },
                  }}
                >
                  {isSaving ? "Zapisywanie..." : "Zapisz"}
                </Button>
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}
